import { useEffect, useMemo, useState } from 'react';
import {
  AssignmentKindType,
  AssignmentRecordingItem,
  AssignmentStatusDetailByClassItem,
} from '../../../../constants/types/lms/assignment/type';
import { useAppDispatch } from '../../../../store/hook';
import TextWinds from '../../../common/TextWinds';
import {
  getProgressColorClassNames,
  getProgressRate,
  getStatusClassName,
  getStatusLabel,
  getTestScoreColorClassName,
} from '../../../../pages/lms/assignment-mgmt/AssignmentStatus';
import { DocumentCheckIcon, PlayIcon } from '@heroicons/react/24/outline';
import { openAlert } from '../../../../store/features/modalSlice';
import {
  openTodoView,
  resolveJebsplayerUrl,
} from '../../../../utils/openContentsWindow';
import { getAssignmentRecordingList } from '../../../../api/utils/lms/assignment/api';
import {
  formatToKstDate,
  getErrorMessages,
} from '../../../../api/utils/util-func';
import RecordingAnswerPanel from './RecordingPanel';

interface AssignmentStatusStudentRowProps {
  assignmentKind: AssignmentKindType;
  assignmentTitle: string;
  detail: AssignmentStatusDetailByClassItem;
}

/**
 * 개편 LMS (교사) - 과제 관리 > 과제 현황 페이지 > 반별 과제 현황 > 반별 과제 카드 > 학생 과제 목록 > 학생 과제 행
 */
const AssignmentStatusStudentRow = ({
  assignmentKind,
  assignmentTitle,
  detail,
}: AssignmentStatusStudentRowProps) => {
  const { student, progress } = detail;

  const dispatch = useAppDispatch();

  const setTodoView = useState<Window>(undefined)[1];

  const progressRate = getProgressRate(progress.progress);

  const isCompleted =
    progress.progressState === 'COMPLETED' || progressRate >= 100;

  const {
    barClassName: progressBarColorClassName,
    textClassName: progressTextColorClassName,
  } = getProgressColorClassNames({
    progressRate,
    isCompleted,
  });

  const testScore = progress.testScore ?? 0;

  const testItems = progress.testItems ?? [];

  const totalQuestionCount = testItems.length;
  const correctQuestionCount = testItems.filter((item) => item.correct).length;

  const testResultText = `(${correctQuestionCount}/${totalQuestionCount})`;

  const hasTestItems =
    assignmentKind === AssignmentKindType.TEST &&
    Array.isArray(progress.testItems) &&
    progress.testItems.length > 0;

  const handleShowResult = () => {
    if (!progress.playerLink) {
      dispatch(
        openAlert({
          message: '과제 링크가 없습니다. 관리자에게 문의해주세요.',
        }),
      );
      return;
    }

    openTodoView(
      { url: resolveJebsplayerUrl(progress.playerLink) },
      setTodoView,
    );
  };

  const [recordingList, setRecordingList] = useState<AssignmentRecordingItem[]>(
    [],
  );

  const [isRecordingPanelOpened, setIsRecordingPanelOpened] = useState(false);

  const fetchAssignmentRecordingList = async () => {
    try {
      const response = await getAssignmentRecordingList({
        studentId: student.id,
        todoId: progress.id,
      });

      setRecordingList(response.items ?? []);
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    }
  };

  useEffect(() => {
    if (progress.recordingCount <= 0) return;

    fetchAssignmentRecordingList();
  }, [progress]);

  const recordingListByContentSeq = useMemo(() => {
    const latestRecordingMapByContentSeq = recordingList.reduce<
      Record<number, (typeof recordingList)[number]>
    >((acc, recording) => {
      if (recording.contentSeq == null) {
        return acc;
      }

      const prevRecording = acc[recording.contentSeq];

      if (!prevRecording) {
        acc[recording.contentSeq] = recording;
        return acc;
      }

      const prevTime = new Date(prevRecording.createdAt).getTime();
      const currentTime = new Date(recording.createdAt).getTime();

      if (currentTime > prevTime) {
        acc[recording.contentSeq] = recording;
      }

      return acc;
    }, {});

    return Object.entries(latestRecordingMapByContentSeq)
      .map(([contentSeq, recording]) => ({
        contentSeq: Number(contentSeq),
        recording,
      }))
      .sort((a, b) => a.contentSeq - b.contentSeq);
  }, [recordingList]);

  const submittedRecordingCount = recordingListByContentSeq.length;
  const hasRecordingItems = submittedRecordingCount > 0;

  return (
    <>
      <div className="border-b border-gray2 last:border-b-0">
        <div className="grid gap-3 px-4 py-3 xl:grid-cols-[200px_360px_200px_minmax(180px,1fr)] xl:items-center">
          <div className="flex min-w-0 items-center gap-1.5 xl:block">
            <TextWinds
              type="content_body2"
              color="gray9"
              className="min-w-0 truncate font-bold leading-tight"
            >
              {student.name}
            </TextWinds>
            <TextWinds
              type="caption1"
              color="gray7"
              className="shrink-0 leading-tight xl:block"
            >
              {student.uid}
            </TextWinds>
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray1">
                <div
                  className={[
                    'h-full rounded-full transition-all duration-300',
                    progressBarColorClassName,
                  ].join(' ')}
                  style={{ width: `${progressRate}%` }}
                />
              </div>

              <TextWinds
                type="caption1"
                className={[
                  'w-[40px] shrink-0 text-right font-bold',
                  progressTextColorClassName,
                ].join(' ')}
              >
                {progressRate}%
              </TextWinds>
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <TextWinds type="caption1" color="gray7">
                학습 회차{' '}
                <span className="font-bold text-gray9">
                  {progress.completedCount ?? 0}
                </span>
                회
              </TextWinds>

              <span className="text-xs text-gray5">·</span>

              <TextWinds type="caption1" color="gray7">
                완료일{' '}
                <span className="font-bold text-gray9">
                  {progress.completedDate
                    ? formatToKstDate(
                        progress.completedDate,
                        'YYYY.MM.DD HH:mm',
                      )
                    : '-'}
                </span>
              </TextWinds>
            </div>
          </div>

          <div className="flex flex-nowrap items-center gap-2 xl:justify-end">
            {assignmentKind === AssignmentKindType.TEST && (
              <div className="flex shrink-0 flex-nowrap items-center gap-2">
                <TextWinds
                  type="content_body2"
                  className={[
                    'shrink-0 whitespace-nowrap font-bold',
                    getTestScoreColorClassName(testScore),
                  ].join(' ')}
                >
                  {testScore}점
                </TextWinds>

                {testResultText && (
                  <TextWinds
                    type="content_body4"
                    color="gray7"
                    className="shrink-0 whitespace-nowrap"
                  >
                    {testResultText}
                  </TextWinds>
                )}

                <button
                  type="button"
                  className={[
                    'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                    hasTestItems
                      ? 'border-[#0B8ED0] bg-white text-[#0B8ED0] hover:bg-blue-50'
                      : 'cursor-not-allowed border-gray3 bg-gray1 text-gray5',
                  ].join(' ')}
                  disabled={!hasTestItems}
                  onClick={handleShowResult}
                >
                  <DocumentCheckIcon className="h-4 w-4" />

                  <span>결과 보기</span>
                </button>
              </div>
            )}

            {hasRecordingItems && (
              <button
                type="button"
                className={[
                  'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                  isRecordingPanelOpened
                    ? 'border-[#0B8ED0] bg-blue-100 text-[#0B8ED0]'
                    : 'border-[#0B8ED0] bg-white text-[#0B8ED0] hover:bg-blue-50',
                ].join(' ')}
                disabled={!hasRecordingItems}
                onClick={() => setIsRecordingPanelOpened((prev) => !prev)}
              >
                <PlayIcon className="h-4 w-4" />
                <span>녹음 듣기</span>
              </button>
            )}

            <span
              className={[
                'shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold',
                getStatusClassName(progress.progressState),
              ].join(' ')}
            >
              {getStatusLabel(progress.progressState)}
            </span>
          </div>
        </div>
      </div>

      {isRecordingPanelOpened && hasRecordingItems && (
        <RecordingAnswerPanel
          studentName={student.name}
          assignmentTitle={assignmentTitle}
          recordingListByContentSeq={recordingListByContentSeq}
        />
      )}
    </>
  );
};

export default AssignmentStatusStudentRow;
